'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type CSSProperties,
} from 'react';

import { isMarketingBannerActive } from 'components/shared/Header/MarketingBanner';
import { Currency } from 'utils/Currency';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { useTranslations } from 'next-intl';
import { useRegionLocale } from 'contexts/RegionLocaleContext';

import { BuyProvider, BuySection } from 'components/buy';
import {
  FAQSection as PhotonFAQSection,
  MuscleRecoverySection,
  PhotonComparison,
  PhotonInTheBox,
  SkinHealthSection,
  TherapySection,
  ThreeStepsSection,
  WavelengthsSection,
  PowerplugSection,
} from 'components/photon';
import {
  photonTechSpecsAccordionItems,
  photonTechSpecsImages,
} from 'components/photon/photonTechSpecsData';
import { ImageModal } from 'components/ring-air/ProductSelectionSection/ImageModal';
import { PrimaryOffersModalData } from 'components/ring-air/ProductSelectionSection/PrimaryOffers';
import { getOffersForRegion } from 'components/ring-air/utils/offersByRegion';
import { useCartStore, useShallow } from 'components/shared/Cart';
import {
  OffersModalNewContext,
  OffersModalNewProvider,
} from 'components/shared/OffersModal';
import { TechnicalSpecs } from 'components/technicalSpecs/TechnicalSpecs';
import {
  PhotonCarouselItems,
  PhotonPrice,
  photonCards,
  productMap_photon,
} from 'content/products/photon';
import { jetbrainsMono } from 'utils/fonts';

import styles from './buy-client.module.css';

interface PhotonBuyPageProps {
  currency: Currency;
  store: ShopifyStore;
  countryCode: string;
}

const PhotonBuyPageContents = ({
  currency,
  store,
  countryCode,
}: PhotonBuyPageProps) => {
  const { setStore } = useCartStore(
    useShallow((s) => ({ setStore: s.setStore })),
  );
  useEffect(() => {
    setStore(store);
  }, [setStore, store]);
  useGlobalUI({ headerCollaspable: true });

  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations('ring');
  const tPhoton = useTranslations('photon');
  const { region, regionSlug } = useRegionLocale();
  const OfferModalNewClient = useContext(OffersModalNewContext);

  // Photon is out of stock: no ship-date copy on the product heading.
  const dispatchDate = null;

  const { offersData, splitPaymentsDisplay } = useMemo(() => {
    const { offersData: data } = getOffersForRegion(
      region || countryCode || 'US',
      {
        t,
        regionSlug,
        router: {
          push: (url: string) => router.push(url),
          query: Object.fromEntries(searchParams?.entries() ?? []),
        },
        product: 'photon',
      },
    );
    return {
      offersData: data,
      splitPaymentsDisplay:
        data.find((offer) => offer.displayValue)?.displayValue ?? null,
    };
  }, [region, countryCode, t, regionSlug, router, searchParams]);

  const handleSeeAllPaymentOptions = useCallback(() => {
    if (!offersData || offersData.length === 0) return;
    OfferModalNewClient.set(
      <ImageModal>
        <PrimaryOffersModalData data={offersData} />
      </ImageModal>,
    );
    OfferModalNewClient.show();
  }, [offersData, OfferModalNewClient]);

  return (
    <BuyProvider
      store={store}
      currency={currency}
      productName="Ultrahuman Photon"
      basePrice={PhotonPrice[store]}
      baseVariantId={productMap_photon.variantId[store]}
      cards={photonCards}
      splitPaymentsDisplay={splitPaymentsDisplay}
      offersData={offersData}
      onSeeAllPaymentOptions={handleSeeAllPaymentOptions}
      dispatchDate={dispatchDate}
    >
      <div
        className={styles.pageContainer}
        style={
          {
            '--photon-buy-pt': isMarketingBannerActive() ? '80px' : '48px',
          } as CSSProperties
        }
      >
        <div className={styles.productContainer}>
          <BuySection
            carouselItems={PhotonCarouselItems}
            title={tPhoton('photon.buyPage.heading.productName')}
            tagline={tPhoton('photon.buyPage.text.tagline')}
            benefitsTitle={tPhoton('photon.buyPage.heading.benefitsTitle')}
            benefits={[
              tPhoton('photon.buyPage.text.benefitSkin'),
              tPhoton('photon.buyPage.text.benefitRecovery'),
              tPhoton('photon.buyPage.text.benefitDiscomfort'),
              tPhoton('photon.buyPage.text.benefitJoint'),
            ]}
            summaryProps={{
              ctaLabel: tPhoton('photon.heroSection.button.outOfStock'),
              isEnabled: () => false,
            }}
          />
        </div>
      </div>
      <div className={jetbrainsMono.variable}>
        <PhotonInTheBox />
        {/* Landing-page section block — Wavelengths + Skin + Muscle lead,
            then the "With you, wherever you go" group, then ThreeSteps /
            Comparison. Mirrors the landing-page order. */}
        <WavelengthsSection />
        <SkinHealthSection />
        <MuscleRecoverySection />
        <TherapySection />
        <PowerplugSection />
        <ThreeStepsSection />
        <PhotonComparison />
        <TechnicalSpecs
          htmlId="photon-specs"
          title={tPhoton('photon.buyPage.heading.technicalSpecs')}
          images={photonTechSpecsImages}
          accordionItems={photonTechSpecsAccordionItems}
        />
        {/* <EndBannerSection /> */}
        <PhotonFAQSection />
      </div>
    </BuyProvider>
  );
};

const PhotonBuyPage = (props: PhotonBuyPageProps) => {
  return (
    <OffersModalNewProvider>
      <PhotonBuyPageContents {...props} />
    </OffersModalNewProvider>
  );
};

export default PhotonBuyPage;
